import { GENERAL_HELP } from "constants/chatType";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useGetUserProfileQuery } from "store/apis/profile";
import { setCreditApplicable, setCredits } from "store/slices/credit";
import * as Sentry from "@sentry/browser";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import DashboardContainer from "components/dashboard/DashboardContainer";
import authContext from "jwt_context&axios/authContext";
import { useToast } from "hooks/useToast";
import { useLazyGetEntitiesQuery } from "store/apis/group";
import { BackendError } from "types/utils/error";
import { FloatingChat } from "components/floatingChat/FloatingChat";
import classNames from "classnames";
import { useAnalytics } from "hooks/useAnalytics";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { openFloatingChat } from "store/slices/chat";
import { useIsInChat } from "hooks/useIsInChat";
import { useChatLocation } from "hooks/useChatLocation";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { AngelSmallRight } from "components/icons/AngelSmallRight";
import { Link } from "react-router-dom";
import { HOMEPAGE_PATHS } from "constants/homepagePaths";
import { SecondaryNavContext } from "components/AppLayout/SecondaryNavContext";
import { PageHeaderSlotContext } from "components/AppLayout/PageHeaderSlotContext";
import { HeaderActionsContext } from "components/AppLayout/HeaderActionsContext";
import { SidePanelSlotContext } from "components/AppLayout/SidePanelSlotContext";
import { BreadcrumbLeadingSlotContext } from "components/AppLayout/BreadcrumbLeadingSlotContext";
import { CommandPaletteProvider } from "components/CommandPalette/CommandPaletteContext";
import { SidebarCollapseIcon } from "components/icons/SidebarCollapseIcon";
import { SidebarExpandIcon } from "components/icons/SidebarExpandIcon";
import ToolTip from "components/design/toolTip";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import { SignupResumeRedirect } from "v2/components/onboarding/SignupResumeRedirect";
import { useCurrentProduct } from "hooks/useCurrentProduct";

type LayoutProps = {
  topbar: ReactNode;
  sidebar: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  noVerticalGap?: boolean;
};

export const AppLayout = ({
  topbar,
  sidebar,
  banner,
  children,
  noVerticalGap = false,
}: LayoutProps) => {
  const {
    isCpa,
    isForeignCA,
    isServiceSuperAdmin,
    isAdmin,
    isInvestor,
    isPreIncorporated,
  } = useRoleBasedView();
  const history = useHistory();
  const location = useLocation();
  const { data: user, error } = useGetUserProfileQuery();
  const dispatch = useAppDispatch();
  const { dispatch: dispatchGroup, uuid: groupId } = useCurrentGroupContext();
  const isV2Platform = useIsGroupV2Platform();
  const { authtoken, setGroupEntities } = useContext(authContext);
  const { alertToast } = useToast();
  const [getEntities] = useLazyGetEntitiesQuery();
  const [bannerShown, setBannerShown] = useState(false);
  const [secHeaderSlot, setSecHeaderSlot] = useState<HTMLElement | null>(null);
  const [secBodySlot, setSecBodySlot] = useState<HTMLElement | null>(null);
  const [secActiveCount, setSecActiveCount] = useState(0);
  const [secCollapsed, setSecCollapsed] = useState(false);
  const [contentHeaderSlot, setContentHeaderSlot] =
    useState<HTMLElement | null>(null);
  const [pageHeaderCount, setPageHeaderCount] = useState(0);
  const [headerActionsSlot, setHeaderActionsSlot] =
    useState<HTMLElement | null>(null);
  const [sidePanelSlot, setSidePanelSlot] = useState<HTMLElement | null>(null);
  const [breadcrumbLeadingSlot, setBreadcrumbLeadingSlot] =
    useState<HTMLElement | null>(null);

  const pageTitle = useSelector(
    (state: RootState) => state.appLayout.pageTitle
  );
  const pageBreadcrumb = useSelector(
    (state: RootState) => state.appLayout.breadcrumb
  );
  const noTopPadding = useSelector(
    (state: RootState) => state.appLayout.noTopPaddingCount > 0
  );

  const registerSecondaryActive = useCallback(() => {
    setSecActiveCount((count) => count + 1);
    setSecCollapsed(false);
    return () => setSecActiveCount((count) => Math.max(0, count - 1));
  }, []);

  const registerPageHeader = useCallback(() => {
    setPageHeaderCount((count) => count + 1);
    return () => setPageHeaderCount((count) => Math.max(0, count - 1));
  }, []);

  const pageHeaderValue = useMemo(
    () => ({ slot: contentHeaderSlot, register: registerPageHeader }),
    [contentHeaderSlot, registerPageHeader]
  );

  const headerActionsValue = useMemo(
    () => ({ slot: headerActionsSlot }),
    [headerActionsSlot]
  );

  const sidePanelValue = useMemo(
    () => ({ slot: sidePanelSlot }),
    [sidePanelSlot]
  );

  const breadcrumbLeadingValue = useMemo(
    () => ({ slot: breadcrumbLeadingSlot }),
    [breadcrumbLeadingSlot]
  );

  const hasPageHeader = pageHeaderCount > 0;

  const secondaryActive = secActiveCount > 0;

  const secondaryNavValue = useMemo(
    () => ({
      headerSlot: secHeaderSlot,
      bodySlot: secBodySlot,
      active: secondaryActive,
      collapsed: secCollapsed,
      setCollapsed: setSecCollapsed,
      registerActive: registerSecondaryActive,
    }),
    [
      secHeaderSlot,
      secBodySlot,
      secondaryActive,
      secCollapsed,
      registerSecondaryActive,
    ]
  );

  const showSecondaryColumn = secondaryActive && !secCollapsed;

  const handleCollapseSecondary = () => setSecCollapsed(true);

  const handleExpandSecondary = () => setSecCollapsed(false);

  const showHeaderRow =
    Boolean(pageTitle) ||
    Boolean(pageBreadcrumb) ||
    secondaryActive ||
    hasPageHeader;
  const search = useQuery();
  const { update } = useUpdateQuery();
  const popup = search.get("popupgeneral");
  const { isInChat } = useIsInChat();
  // On mobile, an open chat thread owns the full screen — hide the app top bar.
  const { isChannelOpen } = useChatLocation();
  const { bookkeeping_channel } = useCurrentEntity();
  const { isOnBooks } = useCurrentProduct();

  useEffect(() => {
    if (popup) {
      update({
        query: "popupgeneral",
        value: null,
      });
      if (!isV2Platform) {
        dispatch(openFloatingChat());
      }
    }
  }, [popup, update, isV2Platform]);

  useEffect(() => {
    localStorage.removeItem("magicLinkData");
    if (error) {
      history.push("/signin");
    } else {
      if (isCpa && location.pathname === GENERAL_HELP) history.push("/chat");
      if (user?.credits_available) {
        dispatch(setCredits(user?.credits_available));
      }
      dispatch(setCreditApplicable(Boolean(user?.credits_applicable)));
    }
  }, [user?.credits_applicable, user?.credits_available]);

  const { email, name } = user || {};

  const { identifyUser } = useAnalytics();

  let role = "CUSTOMER";

  if (isCpa) role = "CPA";
  if (isForeignCA) role = "FOREIGN_CA";
  if (isServiceSuperAdmin) role = "SERVICE_SUPER_ADMIN";
  if (isAdmin) role = "ADMIN";
  if (isInvestor) role = "INVESTOR";
  if (isPreIncorporated) role = "PREINCORPORATION";

  useEffect(() => {
    if (email) {
      try {
        identifyUser();
      } catch (error) {}
    }
  }, [identifyUser, email]);

  useEffect(() => {
    if (email) {
      Sentry.setUser({ email: email, username: name, id: email });
    }
  }, [email, name]);

  const getAndSetGroup = useCallback(async () => {
    try {
      const { data } = await getEntities({});
      setGroupEntities({ data });
      if (data?.groups[0]) {
        dispatchGroup({ type: "SET_GROUP", payload: data?.groups[0] });
        localStorage.setItem("docId", data?.groups[0].documents_group_id);
      }
    } catch (error) {
      if (!authtoken.is_public_user) {
        alertToast({ message: (error as BackendError).data?.error?.message });
      }
    }
  }, [authtoken.is_public_user]);

  useEffect(() => {
    getAndSetGroup();
  }, [getAndSetGroup]);

  const floatingChatVisibility = useMemo(() => {
    let notToShowFloatingChat =
      location.pathname.includes("/filings") ||
      location.pathname.includes("/tickets") ||
      location.pathname.includes("/mymessages") ||
      location.pathname === "/books/tp-payments/transfer" ||
      location.pathname.includes("/chat") ||
      location.pathname.includes("/incorporation") ||
      (location.pathname.includes("/books/transactions") &&
        !location.pathname.includes("/reconciliation") &&
        !location.pathname.includes("/rules")) ||
      HOMEPAGE_PATHS.includes(location.pathname);

    if (notToShowFloatingChat) {
      return false;
    } else {
      return true;
    }
  }, [location.pathname]);

  return (
    <>
      <SignupResumeRedirect />
      {/* <CurrentDeadlineBanner setBannerShown={setBannerShown} /> */}
      <CommandPaletteProvider>
        <PageHeaderSlotContext.Provider value={pageHeaderValue}>
          <HeaderActionsContext.Provider value={headerActionsValue}>
            <SecondaryNavContext.Provider value={secondaryNavValue}>
              <SidePanelSlotContext.Provider value={sidePanelValue}>
                <BreadcrumbLeadingSlotContext.Provider
                  value={breadcrumbLeadingValue}
                >
                  <div
                    className={classNames(
                      "t-flex t-gap-3 t-bg-surface-lighter-grey t-pl-3 t-py-3",
                      {
                        "t-h-[calc(100dvh-40px)]": bannerShown,
                        "t-h-[100dvh]": !bannerShown,
                      }
                    )}
                  >
                    <div
                      data-testid="app-sidebar"
                      className="t-hidden md:t-block"
                    >
                      {sidebar}
                    </div>
                    <div className="t-flex t-flex-col t-flex-1 t-relative t-min-w-0 t-pr-3">
                      <div
                        data-testid="app-topbar"
                        className={classNames("t-block md:t-hidden", {
                          "!t-hidden": isChannelOpen,
                        })}
                      >
                        {topbar}
                      </div>
                      {banner && banner}
                      <div className="t-relative t-flex t-flex-1 t-min-h-0 t-overflow-hidden t-rounded-2xl t-bg-surface t-shadow-light-30">
                        <div
                          className={classNames(
                            "t-w-[184px] t-shrink-0 t-flex-col t-border-0 t-border-r t-border-solid t-border-neutral-0",
                            {
                              "t-hidden md:t-flex": showSecondaryColumn,
                              "t-hidden": !showSecondaryColumn,
                            }
                          )}
                        >
                          <div className="t-flex t-h-14 t-shrink-0 t-items-center t-justify-between t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-px-4">
                            <div
                              ref={setSecHeaderSlot}
                              className="t-min-w-0 t-truncate"
                            />
                            <ToolTip text="Collapse" side="right">
                              <button
                                type="button"
                                onClick={handleCollapseSecondary}
                                className="all:unset t-flex t-cursor-pointer t-text-text-30"
                              >
                                <SidebarCollapseIcon />
                              </button>
                            </ToolTip>
                          </div>
                          <div className="t-min-h-0 t-flex-1 t-overflow-y-auto t-py-2 t-px-3">
                            <div ref={setSecBodySlot} />
                          </div>
                        </div>
                        <div className="t-flex t-min-w-0 t-flex-1 t-flex-col">
                          <div
                            className={classNames(
                              "t-z-header t-flex t-min-h-14 t-shrink-0 t-items-center t-gap-3 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-bg-surface t-px-5 t-py-2 sm:t-px-4",
                              { "t-hidden": !showHeaderRow }
                            )}
                          >
                            {secondaryActive && secCollapsed && (
                              <ToolTip text="Expand" side="right">
                                <button
                                  type="button"
                                  onClick={handleExpandSecondary}
                                  className="all:unset t-hidden t-shrink-0 t-cursor-pointer t-text-text-30 md:t-flex"
                                >
                                  <SidebarExpandIcon />
                                </button>
                              </ToolTip>
                            )}
                            <div className="t-flex t-min-w-0 t-flex-1 t-items-center t-gap-2">
                              <div
                                ref={setBreadcrumbLeadingSlot}
                                className="t-flex t-shrink-0 t-items-center empty:t-hidden"
                              />
                              {pageBreadcrumb && (
                                <Link
                                  to={pageBreadcrumb.to}
                                  className="t-flex t-max-w-48 t-shrink-0 t-items-center t-gap-1 t-text-body t-text-neutral-40 t-no-underline hover:t-text-text-60"
                                >
                                  <span className="t-truncate">
                                    {pageBreadcrumb.label}
                                  </span>
                                  <span className="t-flex t-size-3 t-shrink-0 t-items-center t-justify-center">
                                    <AngelSmallRight />
                                  </span>
                                </Link>
                              )}
                              {!hasPageHeader && pageTitle && (
                                <h5 className="!t-m-0 t-text-subtitle-lg t-font-medium t-capitalize t-leading-6 t-text-text-60">
                                  {pageTitle}
                                </h5>
                              )}
                              <div
                                ref={setContentHeaderSlot}
                                className="t-min-w-0 t-flex-1"
                              />
                            </div>
                            <div
                              ref={setHeaderActionsSlot}
                              className="t-flex t-shrink-0 t-items-center t-gap-1 sm:empty:t-hidden"
                            />
                          </div>
                          <div className="t-flex t-min-h-0 t-min-w-0 t-flex-1 t-flex-col">
                            <DashboardContainer
                              className={classNames("t-h-full t-min-h-0", {
                                "t-pt-5": !noTopPadding,
                                "t-gap-4": !isInChat && !noVerticalGap,
                                "t-gap-0": isInChat || noVerticalGap,
                                "t-border-0": isInChat,
                              })}
                            >
                              <DashboardContainer.Content
                                className={classNames("t-transition-all", {
                                  "!t-overflow-hidden": isInChat,
                                })}
                              >
                                {children}
                                {floatingChatVisibility && (
                                  <FloatingChat
                                    filter={
                                      isOnBooks && groupId
                                        ? { group_uuid: { $in: [groupId] } }
                                        : {}
                                    }
                                    defaultChannelId={
                                      isOnBooks && isAdmin
                                        ? bookkeeping_channel
                                        : null
                                    }
                                  />
                                )}
                              </DashboardContainer.Content>
                            </DashboardContainer>
                          </div>
                        </div>
                        <div
                          ref={setSidePanelSlot}
                          data-side-panel-slot="true"
                          className="t-flex t-h-full t-shrink-0"
                        />
                      </div>
                    </div>
                  </div>
                </BreadcrumbLeadingSlotContext.Provider>
              </SidePanelSlotContext.Provider>
            </SecondaryNavContext.Provider>
          </HeaderActionsContext.Provider>
        </PageHeaderSlotContext.Provider>
      </CommandPaletteProvider>
    </>
  );
};
